import { inject, Injectable } from '@angular/core';

import { AuthenticationService } from 'app/core/authentication';
import { IToken } from 'app/core/authentication/models/token';
import { ITokenResponse } from 'app/core/authentication/models/token-response';
import { IUserClaims } from 'app/core/authentication/models/user-claims';
import { UrlService } from 'app/core/http/url.service';
import { DefaultPageRouteService, RouterService } from 'app/core/navigation';
import { TOKEN_STORAGE_KEYS } from '../constants/token-storage-keys';

import { tokenResponseToToken, tokenResponseToUserClaims } from './token-response-mapper';

@Injectable({
  providedIn: 'root'
})
export class LoginService {
  private readonly authenticationService = inject(AuthenticationService);
  private readonly defaultRoutingService = inject(DefaultPageRouteService);
  private readonly router = inject(RouterService);
  private readonly urlService = inject(UrlService);

  public convertToToken(tokenResponse: ITokenResponse): IToken {
    return tokenResponseToToken(tokenResponse);
  }

  public convertToUserClaims(tokenResponse: ITokenResponse): IUserClaims {
    return tokenResponseToUserClaims(tokenResponse);
  }

  public setTokenResponse(tokenResponse: ITokenResponse, preserveIdleActivity = false): void {
    this.urlService.setServerApiUrl(tokenResponse.CoreUrl);

    this.authenticationService.setUserClaims(
      this.convertToUserClaims(tokenResponse),
      preserveIdleActivity ? 'refresh' : 'login');
    this.authenticationService.setTokens(this.convertToToken(tokenResponse));
  }

  public async redirectOnSuccess(skipRedirectToDefaultPage?: boolean): Promise<void> {
    const redirectEDUrl = sessionStorage.getItem(TOKEN_STORAGE_KEYS.POST_EDLINK_REDIRECT);
    if (redirectEDUrl) {
      sessionStorage.removeItem(TOKEN_STORAGE_KEYS.POST_EDLINK_REDIRECT);
      await this.router.navigateByUrl(redirectEDUrl, { replaceUrl: true });
    }
    else {
      const redirectUrl = this.authenticationService.getInterruptedUrl();
      if (redirectUrl) {
        this.authenticationService.setInterruptedUrl('');
        await this.router.navigateByUrl(redirectUrl, { replaceUrl: true });
      }
      else if (!skipRedirectToDefaultPage) {
        await this.defaultRoutingService.routeToDefaultPage();
      }
    }
  }
}
